import interact from 'interactjs';
import {
  Component,
  OnInit,
  ElementRef,
  HostBinding,
  Injector,
  ComponentFactoryResolver,
  ViewChild,
  ViewContainerRef,
  ComponentRef,
  Type,
  HostListener
} from '@angular/core';

import { Fade } from '../../../shared/animations/fade';
import { ResizeOptions } from '../../classes/resize-options';
import { DragOptions } from '../../classes/drag-options';
import { ShellOptions, ShellLayoutParams } from '../../interfaces';
import { WINDOW_REF, WINDOW_DATA } from '../../providers';
import { WindowManager } from '../../services/window-manager.service';
import { ABSTRACT_DATA } from '../../../shared/providers';



@Component({
  selector: 'bm-window-shell',
  templateUrl: './window-shell.component.html',
  styleUrls: ['./window-shell.component.scss'],
  animations: [Fade],
  providers: [{
    provide: WINDOW_REF,
    useExisting: WindowShellComponent
  }]
})

export class WindowShellComponent implements OnInit {

  public name: string;
  public fullScreen: boolean;
  public focused: boolean;

  public embeddedView: Type<any>;
  public options: ShellOptions;
  public ref: ComponentRef<WindowShellComponent>;
  public isMobile: boolean;

  @ViewChild('vcRef', {read: ViewContainerRef, static: true})
  private vcRef: ViewContainerRef;
  private layoutParams: ShellLayoutParams;


  @HostBinding('style.height')
  public get height() {
    return this.fullScreen ? '100%' : this.layoutParams.height;
  }

  @HostBinding('style.width')
  public get width() {
    return this.fullScreen ? '100%' : this.layoutParams.width;
  }

  @HostBinding('style.transform')
  public get transform() {
    return this.fullScreen ? 'none' : this.layoutParams.transform;
  }

  @HostBinding('style.zIndex')
  public get zIndex() {
    return this.focused ? 101 : 100;
  }

  @HostBinding('style.touchAction')
  public get touchAction() {
    return this.isMobile ? 'unset' : 'none';
  }

  @HostBinding('@fade')
  public animation = true;

  @HostListener('mousedown')
  onShellFocus() {
    this.focus();
  }

  constructor(
    public hostView: ElementRef,
    private windowMng: WindowManager,
    private factoryResolver: ComponentFactoryResolver,
    private injector: Injector) {}


  ngOnInit() {

    this.isMobile = !this.options.draggable && !this.options.resizable;

    const initialLayout = {
      transform: 'translate(0,0)',
      xAxis: 0,
      yAxis: 0,
      width: 'auto',
      height: 'auto'
    };

    this.layoutParams = new Proxy(initialLayout, {
      set: (element, property, value) => {
        element[property] = value;
        if (typeof value === 'number' && (property === 'width' || property === 'height')) {
          element[property] = value + 'px';
          return true;
        }
        element.transform = `translate(${element.xAxis}px, ${element.yAxis}px)`;
        return true;
      }
    });


    const {initialHeight, initialWidth, minHeight, minWidth, floating} = this.options;

    const dragHandler = event => {
      this.layoutParams.xAxis += event.dx;
      this.layoutParams.yAxis += event.dy;
    };

    const resizeHandler = event => {
      this.layoutParams.width = event.rect.width;
      this.layoutParams.height = event.rect.height;
      this.layoutParams.xAxis += event.deltaRect.left;
      this.layoutParams.yAxis += event.deltaRect.top;
    };

    this.layoutParams.width = initialWidth;
    this.layoutParams.height = initialHeight;

    this.startInteract(
      this.hostView.nativeElement,
      new DragOptions(floating),
      new ResizeOptions(minWidth, minHeight, floating),
      this.options.draggable ? dragHandler : () => {},
      this.options.resizable ? resizeHandler : () => {}
    );

    const factory = this.factoryResolver.resolveComponentFactory(this.embeddedView);
    const { data } = this.options;
    const embeddedViewInjector = Injector.create([
      {provide: WINDOW_DATA, useValue: data},
      {provide: ABSTRACT_DATA, useValue: data},
    ], this.injector);
    this.vcRef.createComponent(factory, null, embeddedViewInjector);
  }

  public close() {
    this.ref.destroy();
  }

  public focus() {
    this.windowMng.activeWindows.forEach(window => window.instance.focused = false);
    this.focused = true;
  }

  private startInteract(
    target: HTMLElement,
    dragOptions: DragOptions,
    resizeOptions: ResizeOptions,
    dragHandler: (event: Event) => void,
    resizeHandler: (event: Event) => void) {

    interact(target)
    .draggable(dragOptions)
    .resizable(resizeOptions)
    .on('dragmove', dragHandler)
    .on('resizemove', resizeHandler);
  }

}
